import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Box,
    Typography,
    Button,
    Paper,
    TextField,
    Grid,
    IconButton,
    CircularProgress,
    Alert,
    Switch,
    FormControlLabel,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
    ArrowBack as BackIcon,
    CloudUpload as UploadIcon,
    Delete as DeleteIcon,
    Save as SaveIcon,
    Celebration as CelebrationIcon,
} from '@mui/icons-material';
import ApiService from '../../../services/api';
import logger from '../../../utils/logger';

const Container = styled(Box)(({ theme }) => ({
    padding: theme.spacing(3),
    backgroundColor: '#f8f9fa',
    minHeight: '100vh',
}));

const Header = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
}));

const FormPaper = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(4),
    borderRadius: '16px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
}));

const ImageUploadArea = styled(Box)(({ theme }) => ({
    border: '2px dashed #cbd5e1',
    borderRadius: '12px',
    padding: theme.spacing(3),
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s',
    '&:hover': {
        borderColor: '#f59e0b',
        backgroundColor: '#fffbeb',
    },
}));

const ImagePreviewContainer = styled(Box)({
    display: 'flex',
    gap: '12px',
    flexWrap: 'wrap',
    marginTop: '16px',
});

const ImagePreviewItem = styled(Box)({
    position: 'relative',
    width: '120px',
    height: '120px',
});

const PreviewImage = styled('img')({
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    borderRadius: '10px',
    border: '2px solid #e2e8f0',
});

const DeleteImageButton = styled(IconButton)({
    position: 'absolute',
    top: '-8px',
    right: '-8px',
    backgroundColor: '#ef4444',
    color: 'white',
    padding: '4px',
    '&:hover': {
        backgroundColor: '#dc2626',
    },
});

const EmojiPicker = styled(Box)({
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap',
    marginTop: '8px',
});

const EmojiButton = styled(Button)(({ selected }) => ({
    minWidth: '40px',
    height: '40px',
    fontSize: '20px',
    border: selected ? '2px solid #f59e0b' : '1px solid #e2e8f0',
    backgroundColor: selected ? '#fef3c7' : 'white',
    '&:hover': {
        backgroundColor: '#fef3c7',
    },
}));

const COMMON_EMOJIS = ['🎉', '🪔', '🎊', '🎆', '🎄', '🎁', '🌸', '🌺', '🎃', '❤️', '🙏', '✨', '🔥', '🌙', '⭐'];

const AddFestival = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const isEdit = Boolean(id);

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const [formData, setFormData] = useState({
        name: '',
        date: '',
        emoji: '🎉',
        template: '',
        status: true,
        images: [],
    });

    useEffect(() => {
        if (isEdit) {
            fetchFestival();
        }
    }, [id]);

    const fetchFestival = async () => {
        setLoading(true);
        try {
            const res = await ApiService.get(`/admin/festival/get-festival/${id}`);
            const festival = res?.data || res;
            if (festival) {
                setFormData({
                    name: festival.name || '',
                    date: festival.date || '',
                    emoji: festival.emoji || '🎉',
                    template: festival.template || '',
                    status: festival.status !== false,
                    images: festival.images || [],
                });
            }
        } catch (e) {
            logger.error('Failed to load festival:', e);
            setError('Failed to load festival');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (field) => (event) => {
        const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    const handleEmojiSelect = (emoji) => {
        setFormData((prev) => ({ ...prev, emoji }));
    };

    const handleImageUpload = async (event) => {
        const files = Array.from(event.target.files);
        if (formData.images.length + files.length > 4) {
            setError('Maximum 4 images allowed');
            return;
        }

        setError('');

        for (const file of files) {
            try {
                // Convert to base64 and upload
                const reader = new FileReader();
                reader.onload = async (e) => {
                    const base64 = e.target.result;
                    try {
                        const uploadRes = await ApiService.post('/customer/auto-post/upload-image', {
                            imageData: base64,
                            filename: `festival_${Date.now()}_${file.name.split('.')[0]}`,
                        });

                        if (uploadRes?.data?.imageUrl) {
                            const serverUrl = window.location.hostname === 'localhost'
                                ? 'http://localhost:5000'
                                : 'https://api.visibeen.com';
                            const imageUrl = `${serverUrl}${uploadRes.data.imageUrl}`;

                            setFormData((prev) => ({
                                ...prev,
                                images: [...prev.images, imageUrl].slice(0, 4),
                            }));
                        }
                    } catch (uploadErr) {
                        logger.error('Failed to upload image:', uploadErr);
                        // Fallback to base64 if upload fails
                        setFormData((prev) => ({
                            ...prev,
                            images: [...prev.images, base64].slice(0, 4),
                        }));
                    }
                };
                reader.readAsDataURL(file);
            } catch (e) {
                logger.error('Failed to read file:', e);
            }
        }
    };

    const handleRemoveImage = (index) => {
        setFormData((prev) => ({
            ...prev,
            images: prev.images.filter((_, i) => i !== index),
        }));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setError('');
        setSuccess('');

        if (!formData.name.trim()) {
            setError('Festival name is required');
            return;
        }
        if (!formData.date) {
            setError('Festival date is required');
            return;
        }

        setSaving(true);
        try {
            const payload = {
                name: formData.name.trim(),
                date: formData.date,
                emoji: formData.emoji,
                template: formData.template.trim() || null,
                status: formData.status,
                images: formData.images,
            };

            if (isEdit) {
                await ApiService.put(`/admin/festival/update-festival/${id}`, payload);
                setSuccess('Festival updated successfully!');
            } else {
                await ApiService.post('/admin/festival/create-festival', payload);
                setSuccess('Festival created successfully!');
            }

            setTimeout(() => {
                navigate('/admin-dashboard/festivals');
            }, 1500);
        } catch (e) {
            logger.error('Failed to save festival:', e);
            setError(e?.response?.data?.message || 'Failed to save festival');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <Container>
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                    <CircularProgress />
                </Box>
            </Container>
        );
    }

    return (
        <Container>
            <Header>
                <IconButton onClick={() => navigate('/admin-dashboard/festivals')}>
                    <BackIcon />
                </IconButton>
                <CelebrationIcon sx={{ color: '#f59e0b', fontSize: 32 }} />
                <Typography variant="h5" fontWeight={700} color="#1e293b">
                    {isEdit ? 'Edit Festival' : 'Add New Festival'}
                </Typography>
            </Header>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                    {error}
                </Alert>
            )}

            {success && (
                <Alert severity="success" sx={{ mb: 2 }}>
                    {success}
                </Alert>
            )}

            <FormPaper>
                <form onSubmit={handleSubmit}>
                    <Grid container spacing={3}>
                        {/* Festival Name */}
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Festival Name"
                                placeholder="e.g., Diwali, Christmas, Holi"
                                value={formData.name}
                                onChange={handleChange('name')}
                                required
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '10px',
                                    },
                                }}
                            />
                        </Grid>

                        {/* Festival Date */}
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Festival Date"
                                type="date"
                                value={formData.date}
                                onChange={handleChange('date')}
                                required
                                InputLabelProps={{ shrink: true }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '10px',
                                    },
                                }}
                            />
                        </Grid>

                        {/* Template Name */}
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Template ID (optional)"
                                placeholder="e.g., diwali, christmas"
                                value={formData.template}
                                onChange={handleChange('template')}
                                helperText="Used for template identification"
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '10px',
                                    },
                                }}
                            />
                        </Grid>

                        {/* Status */}
                        <Grid item xs={12} md={6}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={formData.status}
                                        onChange={handleChange('status')}
                                        color="primary"
                                    />
                                }
                                label={
                                    <Typography>
                                        Status: <strong>{formData.status ? 'Active' : 'Inactive'}</strong>
                                    </Typography>
                                }
                            />
                        </Grid>

                        {/* Emoji Selection */}
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                                Select Emoji
                            </Typography>
                            <EmojiPicker>
                                {COMMON_EMOJIS.map((emoji) => (
                                    <EmojiButton
                                        key={emoji}
                                        selected={formData.emoji === emoji}
                                        onClick={() => handleEmojiSelect(emoji)}
                                        type="button"
                                    >
                                        {emoji}
                                    </EmojiButton>
                                ))}
                            </EmojiPicker>
                            <TextField
                                size="small"
                                placeholder="Or type custom emoji"
                                value={formData.emoji}
                                onChange={handleChange('emoji')}
                                sx={{ mt: 1, width: '150px' }}
                            />
                        </Grid>

                        {/* Image Upload */}
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                                Festival Images (max 4)
                            </Typography>
                            <input
                                type="file"
                                accept="image/*"
                                multiple
                                id="festival-image-upload"
                                style={{ display: 'none' }}
                                onChange={handleImageUpload}
                            />
                            <label htmlFor="festival-image-upload">
                                <ImageUploadArea>
                                    <UploadIcon sx={{ fontSize: 40, color: '#94a3b8', mb: 1 }} />
                                    <Typography color="textSecondary">
                                        Click to upload images ({formData.images.length}/4)
                                    </Typography>
                                    <Typography variant="caption" color="textSecondary">
                                        JPG, PNG up to 5MB each
                                    </Typography>
                                </ImageUploadArea>
                            </label>

                            {formData.images.length > 0 && (
                                <ImagePreviewContainer>
                                    {formData.images.map((img, idx) => (
                                        <ImagePreviewItem key={idx}>
                                            <PreviewImage src={img} alt={`Festival image ${idx + 1}`} />
                                            <DeleteImageButton
                                                size="small"
                                                onClick={() => handleRemoveImage(idx)}
                                            >
                                                <DeleteIcon sx={{ fontSize: 14 }} />
                                            </DeleteImageButton>
                                        </ImagePreviewItem>
                                    ))}
                                </ImagePreviewContainer>
                            )}
                        </Grid>

                        {/* Submit Button */}
                        <Grid item xs={12}>
                            <Box display="flex" gap={2} justifyContent="flex-end" mt={2}>
                                <Button
                                    variant="outlined"
                                    onClick={() => navigate('/admin-dashboard/festivals')}
                                    disabled={saving}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                                    disabled={saving}
                                    sx={{
                                        background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                                        px: 4,
                                        '&:hover': {
                                            background: 'linear-gradient(135deg, #d97706 0%, #b45309 100%)',
                                        },
                                    }}
                                >
                                    {saving ? 'Saving...' : isEdit ? 'Update Festival' : 'Create Festival'}
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </form>
            </FormPaper>
        </Container>
    );
};

export default AddFestival;
